/**
 * A barra de cima — a única que não muda de sítio.
 *
 * Veio de um print da barra preta com o pedido: *"quero que esse menu, seja
 * fixo, tanto no discord quanto no office2d, assim o botão de office 2d fica
 * nele quando estiver no discord, e enquanto estiver no office2d, o botao sera
 * de discord"*.
 *
 * Antes havia DOIS cabeçalhos distintos — o `.chat__head` do canal e o
 * `.office__head` do escritório —, cada um com a sua metade da porta. Como cada
 * um deles morria com a sua vista, o botão de alternar nascia e morria com ela:
 * mudava de barra, de vizinhos e de posição a cada passagem. Um alvo que se
 * desloca é um alvo que se procura, e procurar o caminho de volta é a única
 * coisa que este botão não pode obrigar ninguém a fazer.
 *
 * Agora é UMA barra, irmã da linha do conteúdo e por cima dela — logo, por cima
 * também da rail —, montada uma vez e nunca desmontada enquanto a app viver. O
 * botão fica sempre no mesmo pixel e diz sempre para ONDE se vai (nunca onde já
 * se está): no chat lê-se «Escritório», no escritório lê-se «Discord». Esse
 * «Discord» é o nome pelo qual o Kawã trata esta metade da app — *"e aqui coloca
 * o nome como discord em vez de chat"* —, e um botão que aponta para casa tem de
 * dizer o nome que a pessoa usa para casa, não o nome técnico da vista. Ao lado do
 * nome do servidor fica um espaço vazio — o `#topbar-slot` —, que é onde o
 * escritório pendura por portal o que só ele tem (a sala onde o avatar está,
 * "quem está onde", o estado da chamada). No chat esse espaço fica vazio e a
 * barra encolhe ao que é: nome + porta.
 *
 * Uma correcção ao "no escritório lê-se «Discord»": lê-se, enquanto o mundo se
 * VÊ. Com a chamada em ecrã cheio por cima dele, o que está no ecrã é o
 * GreenVoice, e a porta voltava a apontar para onde a pessoa já estava. Nesse
 * caso diz «Escritório» e encolhe a chamada ao cartão de canto — a regra não
 * mudou (o botão diz sempre o DESTINO), mudou o que conta como "onde se está".
 *
 * O cabeçalho do canal vive agora DENTRO desta barra (ver `ChannelHead`, em
 * `ChatView`): eram dois cabeçalhos de 48px empilhados, e o de baixo não pagava
 * a altura que ocupava. Continuam a ser níveis diferentes — o servidor à
 * esquerda, o canal aberto à direita da barra que os separa.
 */

import { useStore } from "../store/store";
import { IconGrid, IconHash } from "./Icons";

/**
 * O sítio onde o escritório pendura os seus controlos.
 *
 * Um `id` e não um contexto de React porque o dono do conteúdo (`OfficeSpace`) e
 * o dono do lugar (esta barra) são IRMÃOS na árvore, não pai e filho — e o único
 * antepassado comum é o `AppShell`, que não tem nada que ver com isto. É o mesmo
 * caminho que o `UserCard` e o `VoiceStage` já fazem com o `document.body`.
 */
export const TOPBAR_SLOT_ID = "topbar-slot";

/**
 * O segundo lugar emprestado: a disposição do palco (Grade / Livre), quando a
 * chamada é a superfície principal — *"mova o grade e livre pro header de
 * cima"*. Era uma fila de botões POR CIMA do vídeo, com ~36px de altura, a
 * dizer respeito ao ecrã inteiro; subiu para a barra que já é do ecrã inteiro.
 *
 * Slot próprio e não o mesmo de cima por uma razão de ORDEM: ali dentro já
 * escrevem dois donos diferentes (o escritório e o cabeçalho do canal), e a
 * posição dependeria de quem montasse primeiro. Com dois nós, cada um tem o seu
 * lugar na barra e não há corrida nenhuma a resolver.
 *
 * `display: contents` na folha de estilo: vazio, este nó não existe para o
 * layout — não há um espaço de 10px reservado num canal de texto, onde palco
 * nenhum há.
 */
export const TOPBAR_TOOLS_SLOT_ID = "topbar-tools";

export function TopBar() {
  const activeServerId = useStore((s) => s.activeServerId);
  const serverName = useStore((s) => (s.activeServerId ? s.servers[s.activeServerId]?.name : undefined));
  const viewMode = useStore((s) => s.viewMode);
  const setViewMode = useStore((s) => s.setViewMode);
  // "Estar no escritório" não é o `viewMode` sozinho: com o ecrã da chamada a
  // TAPAR o mundo, quem está no modo escritório está a olhar para o GreenVoice —
  // *"como eu abri a miniatura, o certo ali de cima, em vez de estar escrito
  // discord, estar escrito escritório?"*. Estava: a porta apontava para onde a
  // pessoa já estava. Com a chamada em ecrã cheio o destino é o MUNDO, e a
  // maneira de lá voltar é encolher a chamada ao cartão de canto — que é também
  // a razão de o ecrã cheio já não ter botão nenhum de «Reduzir» (`OfficeSpace`).
  //
  const callFull = useStore((s) => s.officeCallFull);
  const setOfficeCallMin = useStore((s) => s.setOfficeCallMin);

  // Onde se ESTÁ. Tudo o que o botão mostra é o contrário disto — o destino.
  const noEscritorio = viewMode === "office" && !callFull;

  return (
    <header className="topbar">
      {/* Em "@me" não há servidor nenhum — fica a marca, que é o que aquele
          espaço sempre disse. */}
      <h1 className="topbar__name">{serverName ?? "GreenVoice"}</h1>

      {/* O lugar emprestado ao escritório. Vazio no chat, e vazio ocupa espaço
          na mesma: é ele que empurra a porta para o canto direito nas duas
          vistas, e é isso que faz o botão não se mexer. */}
      <div id={TOPBAR_SLOT_ID} className="topbar__slot" />

      {/* A disposição do palco, quando há palco. Fica encostado à porta porque
          é o mesmo tipo de coisa: manda no ecrã inteiro, não no canal. */}
      <div id={TOPBAR_TOOLS_SLOT_ID} className="topbar__tools" />

      {/* A porta. Em "@me" não existe: o escritório é de um servidor. */}
      {activeServerId !== null ? (
        <button
          type="button"
          className="topbar__mode"
          onClick={() => {
            // Três destinos, um botão. Com a chamada a tapar o mundo o caminho de
            // volta NÃO é mudar de vista (já se está no escritório): é encolher a
            // chamada ao canto e devolver o mapa.
            if (callFull) setOfficeCallMin(true);
            else setViewMode(noEscritorio ? "chat" : "office");
          }}
          title={noEscritorio ? "Voltar ao Discord" : callFull ? "Voltar ao mundo 2D" : "Abrir o escritório 2D"}
        >
          {noEscritorio ? <IconHash size={15} /> : <IconGrid size={15} />}
          <span>{noEscritorio ? "Discord" : "Escritório"}</span>
        </button>
      ) : null}
    </header>
  );
}
